import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Timer, Zap, Swords, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { usePageTitle } from '@hooks/usePageTitle';
import { useCopyScoring } from '../hooks/useCopyScoring';
import { HEADLINE_DUEL_EXERCISES } from '../data/copywritingExercises';
import CopyEditor from '../components/CopyEditor';
import CopyScoreDisplay from '../components/CopyScoreDisplay';

const ACCENT = '#7c3aed';
const ACCENT_LIGHT = 'rgba(124,58,237,0.10)';
const ACCENT_SHADOW = 'rgba(124,58,237,0.45)';

const HeadlineDuel = () => {
  usePageTitle('Headline Duel | Castle Atlas');
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { scoreExercise, loading, error, clearError } = useCopyScoring();

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );

  const [currentIdx, setCurrentIdx] = useState(0);
  const [userHeadline, setUserHeadline] = useState('');   // beat-control / revision
  const [justification, setJustification] = useState(''); // pick-winner
  const [choice, setChoice] = useState(null);              // pick-winner: 'A' | 'B'
  const [timeLeft, setTimeLeft] = useState(60);
  const [isRunning, setIsRunning] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(null);

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (exerciseId) {
      const idx = HEADLINE_DUEL_EXERCISES.findIndex(e => e.id === exerciseId);
      if (idx >= 0) setCurrentIdx(idx);
    }
  }, [exerciseId]);

  const exercise = HEADLINE_DUEL_EXERCISES[currentIdx];
  const isPickWinner = exercise?.mode === 'pick-winner';

  const canSubmit = isPickWinner
    ? Boolean(choice) && justification.trim().length > 0
    : userHeadline.trim().length > 0;

  const handleSubmit = useCallback(async () => {
    if (submitted || !canSubmit) return;
    setSubmitted(true);
    setIsRunning(false);

    try {
      const payload = isPickWinner
        ? {
            exerciseId: exercise.id,
            moduleId: 'headline-duel',
            mode: exercise.mode,
            userChoice: choice,
            userText: justification.trim(),
          }
        : {
            exerciseId: exercise.id,
            moduleId: 'headline-duel',
            mode: exercise.mode,
            userText: userHeadline.trim(),
          };
      const res = await scoreExercise(payload);
      setResult(res);
      setScore(res.scores?.overall ?? 0);
    } catch (err) {
      // unlock so the student can retry; the hook holds the message shown under the button
      console.error(err);
      setSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, canSubmit, isPickWinner, choice, justification, userHeadline, exercise, scoreExercise]);

  // Timer — starts on first interaction, auto-submits at zero
  useEffect(() => {
    if (!isRunning || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, timeLeft]);

  const startTimer = useCallback(() => {
    if (!isRunning && !submitted) setIsRunning(true);
  }, [isRunning, submitted]);

  const nextExercise = () => {
    if (currentIdx < HEADLINE_DUEL_EXERCISES.length - 1) {
      setCurrentIdx(prev => prev + 1);
      setUserHeadline(''); setJustification(''); setChoice(null);
      setTimeLeft(60); setIsRunning(false);
      setSubmitted(false); setResult(null); setScore(null); clearError();
      navigate(`/skills/copywriting/headline-duel/${HEADLINE_DUEL_EXERCISES[currentIdx + 1].id}`, { replace: true });
    }
  };

  const c = {
    pageBg: isDark ? '#0e0d0c' : '#faf9f7',
    card: isDark ? '#1c1917' : '#ffffff',
    border: isDark ? '#2d2825' : '#e8e4df',
    head: isDark ? '#fafaf9' : '#1c1917',
    muted: isDark ? '#78716c' : '#a8a29e',
    sub: isDark ? '#57534e' : '#b4aca6',
    controlBg: isDark ? 'rgba(124,58,237,0.06)' : 'rgba(124,58,237,0.04)',
    controlBorder: isDark ? '#4c3580' : '#c4b5fd',
    winnerBg: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
    winnerBorder: 'rgba(34,197,94,0.3)',
    loserBg: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)',
    loserBorder: 'rgba(239,68,68,0.3)',
  };

  const AxisPill = ({ label, muted }) => (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 99,
      background: muted ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') : ACCENT_LIGHT,
      color: muted ? c.muted : ACCENT, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>{label}</span>
  );

  if (!exercise) return null;

  return (
    <div style={{ maxWidth: '100%', paddingBottom: 80, background: c.pageBg, minHeight: '100vh' }}>
    <div className="rail-clearance">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);} }
        @keyframes pulse-red { 0%,100%{opacity:1;}50%{opacity:0.6;} }
        .anim-fade { animation: fadeUp 0.3s ease-out both; }
        .timer-urgent { animation: pulse-red 1s ease-in-out infinite; color: #ef4444 !important; }
      `}</style>

      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: isDark ? 'rgba(14,13,12,0.85)' : 'rgba(250,249,247,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${c.border}`,
        padding: '12px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate('/skills/copywriting')} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ArrowLeft size={18} color={c.head} />
        </button>
        <Swords size={16} color={ACCENT} />
        <span style={{ fontSize: 13, fontWeight: 800, color: c.head, flex: 1 }}>Headline Duel</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: c.muted }}>
          {currentIdx + 1} / {HEADLINE_DUEL_EXERCISES.length}
        </span>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 12px', borderRadius: 99,
          background: timeLeft <= 10 ? 'rgba(239,68,68,0.1)' : ACCENT_LIGHT,
          border: `1px solid ${timeLeft <= 10 ? 'rgba(239,68,68,0.3)' : `${ACCENT}20`}`,
        }} className={timeLeft <= 10 && isRunning ? 'timer-urgent' : ''}>
          <Timer size={12} color={timeLeft <= 10 ? '#ef4444' : ACCENT} />
          <span style={{ fontSize: 12, fontWeight: 800, color: timeLeft <= 10 ? '#ef4444' : ACCENT, fontVariantNumeric: 'tabular-nums' }}>
            {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
          </span>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: isMobile ? '16px 16px 40px' : '24px 24px 60px' }}>
        {/* Brief Card */}
        <div className="anim-fade" style={{
          background: c.card, border: `1px solid ${c.border}`,
          borderRadius: 20, padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <AxisPill label={exercise.primaryAxis} />
            {exercise.secondaryAxes?.map(a => <AxisPill key={a} label={a} muted />)}
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: c.head, marginBottom: 10, lineHeight: 1.3 }}>
            {exercise.title}
          </h2>
          <p style={{ fontSize: 13, color: c.sub, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {exercise.briefText}
          </p>
        </div>

        {isPickWinner ? (
          <>
            {/* Two options */}
            <div className="anim-fade" style={{
              display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 20,
            }}>
              {[['A', exercise.optionA], ['B', exercise.optionB]].map(([label, text]) => (
                <button key={label} onClick={() => { setChoice(label); startTimer(); }} disabled={submitted} style={{
                  textAlign: 'left', padding: '16px 18px', borderRadius: 16, cursor: submitted ? 'default' : 'pointer',
                  background: choice === label ? ACCENT_LIGHT : c.card,
                  border: `1.5px solid ${choice === label ? ACCENT : c.border}`,
                  transition: 'all 0.15s ease',
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: 7, marginBottom: 10,
                    background: choice === label ? ACCENT : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                    color: choice === label ? '#fff' : c.muted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800,
                  }}>{label}</div>
                  <div style={{ fontSize: 13, color: c.head, lineHeight: 1.5 }}>{text}</div>
                </button>
              ))}
            </div>

            <div className="anim-fade" style={{ marginBottom: 20 }}>
              <CopyEditor
                key={`${exercise.id}:justification`}
                draftKey={`${exercise.id}:justification`}
                value={justification}
                onChange={(val) => { setJustification(val); startTimer(); }}
                placeholder="Name the principle and explain your pick..."
                label="Your justification"
                rows={3}
                isDark={isDark}
                disabled={submitted}
              />
            </div>
          </>
        ) : (
          <>
            {exercise.control && (
              <div className="anim-fade" style={{
                background: c.controlBg, border: `1.5px solid ${c.controlBorder}`,
                borderRadius: 16, padding: '14px 18px', marginBottom: 16,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  Control
                </div>
                <div style={{ fontSize: 14, color: c.head, fontWeight: 600 }}>{exercise.control}</div>
              </div>
            )}
            <div className="anim-fade" style={{ marginBottom: 20 }}>
              <CopyEditor
                key={`${exercise.id}:headline`}
                draftKey={`${exercise.id}:headline`}
                value={userHeadline}
                onChange={(val) => { setUserHeadline(val); startTimer(); }}
                placeholder={exercise.mode === 'revision' ? 'Your revised headline...' : 'Your headline...'}
                label={exercise.mode === 'revision' ? 'Your revision' : 'Your headline'}
                maxLength={120}
                rows={2}
                isDark={isDark}
                autoFocus
                disabled={submitted}
              />
            </div>
          </>
        )}

        {/* Submit */}
        {!submitted && (
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            style={{
              width: '100%', padding: '14px 24px', borderRadius: 12,
              background: (!canSubmit || loading) ? (isDark ? '#2d2825' : '#e8e4df') : ACCENT,
              color: (!canSubmit || loading) ? c.muted : '#fff',
              fontSize: 14, fontWeight: 700, border: 'none', cursor: (!canSubmit || loading) ? 'not-allowed' : 'pointer',
              boxShadow: (!canSubmit || loading) ? 'none' : `0 4px 20px -4px ${ACCENT_SHADOW}`,
              transition: 'all 0.15s ease',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Zap size={15} />
            {loading ? 'Scoring...' : isPickWinner ? 'Submit Pick' : 'Submit Headline'}
          </button>
        )}
        {error && !submitted && (
          <p role="alert" style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: '#ef4444', lineHeight: 1.5 }}>
            {error} Your answer is still here; try again.
          </p>
        )}

        {/* Results */}
        {submitted && result && (
          <div className="anim-fade" style={{ marginTop: 24 }}>
            {isPickWinner && typeof result.pickCorrect === 'boolean' && (
              <div style={{
                marginBottom: 16, padding: '14px 18px', borderRadius: 16,
                background: result.pickCorrect ? c.winnerBg : c.loserBg,
                border: `1.5px solid ${result.pickCorrect ? c.winnerBorder : c.loserBorder}`,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                {result.pickCorrect
                  ? <CheckCircle2 size={20} color="#22c55e" />
                  : <XCircle size={20} color="#ef4444" />}
                <span style={{ fontSize: 13, fontWeight: 700, color: result.pickCorrect ? '#22c55e' : '#ef4444' }}>
                  {result.pickCorrect ? 'Correct pick' : `Worth another look — ${result.correctChoice} was the stronger option here`}
                </span>
              </div>
            )}

            <CopyScoreDisplay
              score={score}
              isNew
              isDark={isDark}
              extraScores={result.scores}
              feedback={result.feedback}
            />

            {currentIdx < HEADLINE_DUEL_EXERCISES.length - 1 && (
              <button
                onClick={nextExercise}
                style={{
                  width: '100%', marginTop: 16, padding: '14px 24px', borderRadius: 12,
                  background: ACCENT, color: '#fff',
                  fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
                  boxShadow: `0 4px 20px -4px ${ACCENT_SHADOW}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                Next Exercise <ChevronRight size={15} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default HeadlineDuel;
